/**
 * PLANTILLAS INTERNAS DE NOTIFICACIÓN — el catálogo y su aritmética, puros.
 *
 * Pedido de Irving (2026-08-31): poder EDITAR desde el admin los textos de los
 * avisos internos — los 6 del cliente, los del staff y el de puntos del pase de
 * Google Wallet — sin deploy, igual que ya se revinculan las plantillas de
 * WhatsApp. (El aviso de CERCANÍA del pase NO está aquí: Google no acepta texto
 * propio en la notificación de cercanía — solo asoma el pase; Apple sí lo
 * permitirá vía `relevantText` cuando llegue Apple Wallet.)
 *
 * ⚠️ **PARIDAD: réplica EXACTA en krispis-admin, krispis-web y krispis-server**
 * (`api/NotificationTemplates/NotificationTemplates.ts`). Aquí viven los textos
 * POR DEFECTO: si las copias se separan, el mismo aviso saldría con un texto
 * distinto según quién lo emita (el tablero del admin o el webhook del server).
 *
 * ## Cómo funciona la edición
 *
 * La colección **`notification-templates`** guarda SOLO los overrides (un doc
 * por plantilla, con `title`/`body` opcionales). Los emisores resuelven:
 * override si existe, si no el texto de este catálogo — **el fallback vive en
 * el código a propósito**, mismo patrón probado de `message-flows`: una
 * pantalla de configuración a medias o un Firestore caído jamás pueden dejar
 * un aviso sin texto.
 *
 * Las variables van como `{nombre}` y las llena `fillTemplate`. Un token que
 * el emisor no conoce se queda visible tal cual — mejor un `{typo}` a la vista
 * que un hueco silencioso.
 */

export type NotificationTemplateGroup = 'cliente' | 'staff' | 'wallet';

export interface NotificationTemplateDef {
    key: string;
    group: NotificationTemplateGroup;
    /** Nombre humano en la pantalla de administración. */
    label: string;
    /** Cuándo se manda — para que quien edite sepa qué está tocando. */
    description: string;
    title: string;
    body: string;
    /** Variables disponibles, para mostrarlas en el editor. */
    vars: string[];
}

export interface NotificationTemplateOverride {
    title?: string;
    body?: string;
    updatedAt?: number;
    updatedBy?: string;
}

export type NotificationTemplateOverrides = Record<string, NotificationTemplateOverride | undefined>;

export const NOTIFICATION_TEMPLATES_COLLECTION = 'notification-templates';

export const NOTIFICATION_TEMPLATES: NotificationTemplateDef[] = [
    // ── Cliente: su campana y su push, por estado del pedido ──────────────
    {
        key: 'customer_paid',
        group: 'cliente',
        label: 'Pedido confirmado',
        description: 'Cuando el pago entra (web) o el POS lo marca pagado.',
        title: 'Confirmamos tu pedido',
        body: 'Ya lo recibimos. Te avisamos en cuanto esté listo.',
        vars: []
    },
    {
        key: 'customer_preparing',
        group: 'cliente',
        label: 'En preparación',
        description: 'Cuando cocina empieza el pedido.',
        title: 'Estamos preparando tu pedido',
        body: 'Sale de la cocina en unos minutos.',
        vars: []
    },
    {
        key: 'customer_ready_delivery',
        group: 'cliente',
        label: 'Listo (a domicilio)',
        description: 'Pedido de delivery listo, a punto de salir.',
        title: 'Tu pedido está listo',
        body: 'En un momento sale hacia tu dirección.',
        vars: []
    },
    {
        key: 'customer_ready_pickup',
        group: 'cliente',
        label: 'Listo (para recoger)',
        description: 'Pedido de mostrador, kiosco o pickup listo.',
        title: '¡Tu pedido está listo!',
        body: 'Ya puedes pasar por él.',
        vars: []
    },
    {
        key: 'customer_ready_mesa',
        group: 'cliente',
        label: 'Listo (mesa)',
        description: 'Pedido de mesa listo para llevarse a la mesa.',
        title: '¡Tu pedido está listo!',
        body: 'Te lo llevamos a tu mesa.',
        vars: []
    },
    {
        key: 'customer_route',
        group: 'cliente',
        label: 'En camino',
        description: 'El repartidor va en ruta (delivery).',
        title: 'Tu pedido va en camino',
        body: 'Te avisamos cuando llegue.',
        vars: []
    },
    {
        key: 'customer_completed_puntos',
        group: 'cliente',
        label: 'Entregado (con puntos)',
        description: 'Pedido completado que otorgó puntos.',
        title: '¡Gracias por tu compra!',
        body: 'Ganaste {puntos} puntos Krispis.',
        vars: ['puntos']
    },
    {
        key: 'customer_completed',
        group: 'cliente',
        label: 'Entregado (sin puntos)',
        description: 'Pedido completado sin puntos que anunciar.',
        title: '¡Gracias por tu compra!',
        body: 'Esperamos verte pronto.',
        vars: []
    },
    {
        key: 'customer_cancelled',
        group: 'cliente',
        label: 'Cancelado',
        description: 'Cuando el pedido se cancela (staff o pago fallido).',
        title: 'Tu pedido fue cancelado',
        body: 'Si tienes alguna duda, escríbenos.',
        vars: []
    },

    // ── Staff: la campana interna ─────────────────────────────────────────
    {
        key: 'staff_new_delivery_order',
        group: 'staff',
        label: 'Pedido nuevo a domicilio',
        description: 'A la sucursal cuando entra un pedido web de delivery pagado.',
        title: 'Pedido nuevo a domicilio',
        body: '{cliente} · ${total}',
        vars: ['cliente', 'total']
    },
    {
        key: 'staff_delayed_order',
        group: 'staff',
        label: 'Pedido retrasado',
        description: 'Cuando un pedido lleva demasiado en el tablero de cocina.',
        title: 'Pedido retrasado · {folio}',
        body: 'Lleva más de {minutos} minutos esperando.',
        vars: ['folio', 'minutos']
    },
    {
        key: 'staff_cash_register',
        group: 'staff',
        label: 'Caja sin cerrar',
        description: 'Cuando quedó una caja abierta de un día anterior.',
        title: 'Hay una caja sin cerrar',
        body: 'Quedó abierta desde el {desde}{usuario}. Ciérrala desde el panel de Cajas.',
        vars: ['desde', 'usuario']
    },
    {
        key: 'staff_service_off',
        group: 'staff',
        label: 'Servicio apagado',
        description: 'Cuando alguien apaga Pickup o Delivery en una sucursal, con el motivo que dio.',
        title: '{servicio} apagado en {sucursal}',
        body: 'Motivo: {motivo}',
        vars: ['servicio', 'sucursal', 'motivo']
    },
    {
        key: 'staff_stock_zero',
        group: 'staff',
        label: 'Stock en cero',
        description: 'Cuando un insumo llega exactamente a cero.',
        title: 'Se acabó: {insumo}',
        body: 'Llegó a cero. Considera marcarlo como agotado si ya no hay.',
        vars: ['insumo']
    },
    {
        key: 'staff_stock_negative',
        group: 'staff',
        label: 'Stock negativo',
        description: 'Cuando un insumo queda por debajo de cero.',
        title: 'Se acabó: {insumo}',
        body: 'Quedó en {saldo} — hay que revisar el conteo o pedir al CEDIS.',
        vars: ['insumo', 'saldo']
    },

    // ── Wallet: el mensaje del pase (TEXT_AND_NOTIFY) ─────────────────────
    {
        key: 'wallet_points_earned',
        group: 'wallet',
        label: 'Puntos ganados (pase)',
        description:
            'La notificación del pase de Google Wallet al ganar puntos. Cuota: 3 por pase cada 24 h.',
        title: 'Ganaste {movimiento}',
        body: 'Ya tienes {saldo} en tu tarjeta Krispis. ¡Gracias por tu compra!',
        vars: ['movimiento', 'saldo']
    },
    {
        key: 'wallet_points_spent',
        group: 'wallet',
        label: 'Puntos usados (pase)',
        description: 'La notificación del pase al canjear puntos.',
        title: 'Usaste {movimiento}',
        body: 'Te {quedan} {saldo} en tu tarjeta Krispis.',
        vars: ['movimiento', 'saldo', 'quedan']
    }
];

export const getTemplateDef = (key: string): NotificationTemplateDef | undefined =>
    NOTIFICATION_TEMPLATES.find((template) => template.key === key);

/**
 * Rellena `{tokens}`. Un token sin valor se queda VISIBLE tal cual: mejor un
 * `{typo}` a la vista de quien edita que un hueco silencioso — la lección de
 * las plantillas de WhatsApp, donde una variable vacía cancelaba el mensaje.
 */
export const fillTemplate = (text: string, vars: Record<string, string | number> = {}): string =>
    text.replace(/\{(\w+)\}/g, (token, name) =>
        vars[name] !== undefined ? String(vars[name]) : token
    );

/**
 * El texto FINAL de una plantilla: override si existe (por campo — se puede
 * personalizar solo el cuerpo), si no el del catálogo; y las variables
 * rellenadas al final, así el override también puede usarlas.
 */
export const resolveTemplateTexts = (
    key: string,
    vars: Record<string, string | number> = {},
    overrides?: NotificationTemplateOverrides
): { title: string; body: string } => {
    const def = getTemplateDef(key);
    const override = overrides?.[key];
    const title = override?.title?.trim() || def?.title || '';
    const body = override?.body?.trim() || def?.body || '';
    return { title: fillTemplate(title, vars), body: fillTemplate(body, vars) };
};
